/**
 * @file milkPowderDates.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-08
 * @description Milk powder customer prices run From one day To another
 *              (pricing.milk_powder_contract_prices.valid_from / valid_to,
 *              both inclusive). Before migrations/milk_powder_contract_dates.sql
 *              a price was one calendar month (price_month, the 1st), and a row
 *              without the dates still reads as that whole month. Pure — no
 *              database, no React.
 *
 *              Written once here and copied to the server as
 *              utils/milkPowderDates.js by scripts/sync-shared-utils.js, so the
 *              screen and the server agree on what a range is, when two
 *              overlap and which price covers a delivery date. Edit this file
 *              and re-run the script.
 *
 *              Days are 'YYYY-MM-DD' strings and are compared as strings. A
 *              Date is read as its London calendar day.
 */

import { formatDateDDMMYY } from './dateFormatters';

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Is 'YYYY-MM-DD' a real calendar day (no 31 April)? */
export function isRealDay(day) {
  const m = DAY_RE.exec(String(day || ''));
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

/**
 * A value as a 'YYYY-MM-DD' day, or null. A string is taken as already a day
 * (Postgres DATE columns arrive as strings); a Date is read in London time.
 */
export function toDay(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
  }
  const s = String(value).trim().slice(0, 10);
  return isRealDay(s) ? s : null;
}

/** The 1st of the day's month. */
export function monthStartOf(value) {
  const day = toDay(value);
  return day ? `${day.slice(0, 7)}-01` : null;
}

/** The last day of the day's month. */
export function monthEndOf(value) {
  const day = toDay(value);
  if (!day) return null;
  const y = Number(day.slice(0, 4));
  const mo = Number(day.slice(5, 7));
  const last = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  return `${day.slice(0, 7)}-${String(last).padStart(2, '0')}`;
}

/** The whole month the day is in, as a range. */
export function monthRangeOf(value) {
  const from = monthStartOf(value);
  return from ? { from, to: monthEndOf(from) } : null;
}

/**
 * The month ('YYYY-MM-01') when From–To is exactly one whole calendar month,
 * else null. Before the migration that is the only range a price can hold.
 */
export function wholeMonthOf(from, to) {
  const f = toDay(from);
  const t = toDay(to);
  if (!f || !t) return null;
  return f === monthStartOf(f) && t === monthEndOf(f) ? f : null;
}

/**
 * A price row's range. Its own dates when it has them, else the month of its
 * price_month (a row from before the migration, or a server without it).
 */
export function rangeOf(row) {
  if (!row) return null;
  const from = toDay(row.valid_from);
  const to = toDay(row.valid_to);
  if (from && to) return { from, to };
  return monthRangeOf(row.price_month);
}

/** Why From–To cannot be saved, in a sentence — or null when it can. */
export function rangeProblem(from, to) {
  if (from === null || from === undefined || from === '') return 'Enter the From date.';
  if (to === null || to === undefined || to === '') return 'Enter the To date.';
  const f = toDay(from);
  const t = toDay(to);
  if (!f) return 'The From date is not a valid date.';
  if (!t) return 'The To date is not a valid date.';
  if (t < f) return `The To date (${formatDateDDMMYY(t)}) is before the From date (${formatDateDDMMYY(f)}).`;
  return null;
}

/** Do two ranges share at least one day? Both ends count. */
export function rangesOverlap(a, b) {
  if (!a || !b || !a.from || !a.to || !b.from || !b.to) return false;
  return a.from <= b.to && b.from <= a.to;
}

/** Is the day inside the price's range? */
export function coversDay(row, value) {
  const day = toDay(value);
  const r = rangeOf(row);
  return !!(day && r) && r.from <= day && day <= r.to;
}

/**
 * The first of `rows` (one customer + product's prices) whose range overlaps
 * From–To, leaving out the price being edited. Null when none does.
 */
export function findOverlap(range, rows, { excludeId = null } = {}) {
  if (!range || !Array.isArray(rows)) return null;
  return rows.find(r => (excludeId == null || String(r.id) !== String(excludeId))
    && rangesOverlap(range, rangeOf(r))) || null;
}

/** '01/10/26 – 31/10/26'. */
export function formatRange(rowOrRange) {
  const r = rowOrRange && rowOrRange.from && rowOrRange.to ? rowOrRange : rangeOf(rowOrRange);
  if (!r) return '-';
  return `${formatDateDDMMYY(r.from)} – ${formatDateDDMMYY(r.to)}`;
}

/**
 * A short From – To for a table cell: '01/10–31/10/26' within one year,
 * else the full '01/12/26 – 31/01/27'.
 */
export function formatRangeCompact(rowOrRange) {
  const r = rowOrRange && rowOrRange.from && rowOrRange.to ? rowOrRange : rangeOf(rowOrRange);
  if (!r) return '-';
  if (r.from.slice(0, 4) !== r.to.slice(0, 4)) return formatRange(r);
  return `${formatDateDDMMYY(r.from).slice(0, 5)}–${formatDateDDMMYY(r.to)}`;
}

/** The sentence a clash is refused with, naming the other price's dates. */
export function overlapMessage(row) {
  const r = rangeOf(row);
  const price = row && row.sale_price != null && Number.isFinite(Number(row.sale_price))
    ? ` (£${Number(row.sale_price).toFixed(2)} / bag)` : '';
  if (!r) return 'These dates overlap another price for this customer and product.';
  return `These dates overlap the price from ${formatDateDDMMYY(r.from)} to ${formatDateDDMMYY(r.to)}${price}. `
    + 'Change that price\'s dates, or choose dates that do not overlap it.';
}

/**
 * What a new price starts with: the whole of this month (the screen opened on
 * the current month before prices had dates), or — when a price already falls
 * in this month — the day after the latest price To the end of that day's
 * month, the next stretch still to price.
 */
export function defaultRangeFor(rows, today) {
  const day = toDay(today) || toDay(new Date());
  const list = Array.isArray(rows) ? rows : [];
  const month = monthRangeOf(day);
  if (!findOverlap(month, list)) return month;
  const latestTo = list.map(r => rangeOf(r)).filter(Boolean).map(r => r.to).sort().pop();
  const next = new Date(Date.UTC(Number(latestTo.slice(0, 4)), Number(latestTo.slice(5, 7)) - 1, Number(latestTo.slice(8, 10)) + 1));
  const from = next.toISOString().slice(0, 10);
  return { from, to: monthEndOf(from) };
}
